import { useRef, useState } from 'react'
import { Upload, FileText, FileSpreadsheet, Loader2, RotateCcw } from 'lucide-react'
import { Button } from './Button.jsx'

/**
 * <FileDrop> — pick ONE file by dragging it in or clicking. Built for the ตามเบิก
 * importers (ปกส. PDF, สปสช. .xlsx), which had drifted into two different pickers within
 * the same hub: a drop zone on one tab, a lone button on the next.
 *
 *   onFile(file)   a File was dropped or chosen. Validate it in the page (extension, size) —
 *                  the zone only filters what the OS picker offers (`accept`).
 *   fileName       the current file's name, or '' — the zone then shows it and offers ล้าง.
 *                  A NAME, not the File: pages keep it in useEphemeralState across tab flips,
 *                  where a File object would be meaningless.
 *   onClear        → the ล้าง (reset) button. Shown only when there is a file and onClear is
 *                  given. The page clears EVERYTHING derived from the file, not just the name.
 *   busy           reading the file: spinner, no new drops, no reset mid-read.
 *   accept         passed to <input type=file>, e.g. "application/pdf,.pdf".
 *   label, hint    the empty-state lines ("ลากไฟล์ PDF มาวางที่นี่").
 *   kind           'pdf' | 'sheet' — only picks the icon shown once a file is in.
 *
 * The file is never uploaded by this part; the default hint says so, because staff ask.
 */
export function FileDrop({
  onFile, onClear, fileName = '', busy = false, disabled = false,
  accept, label = 'ลากไฟล์มาวางที่นี่', hint, kind = 'pdf', className = '',
}) {
  const ref = useRef(null)
  const [over, setOver] = useState(false)
  const off = busy || disabled
  const FileIcon = kind === 'sheet' ? FileSpreadsheet : FileText

  const pick = (f) => {
    if (ref.current) ref.current.value = ''   // choosing the same file again must re-fire
    if (f && !off) onFile?.(f)
  }

  return (
    <div className={className}>
      <input ref={ref} type="file" accept={accept} className="hidden" onChange={(e) => pick(e.target.files?.[0] ?? null)} />
      <div
        role="button"
        tabIndex={off ? -1 : 0}
        aria-disabled={off}
        onClick={() => { if (!off) ref.current?.click() }}
        onKeyDown={(e) => { if (!off && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); ref.current?.click() } }}
        onDragOver={(e) => { e.preventDefault(); if (!off) setOver(true) }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); pick(e.dataTransfer?.files?.[0] ?? null) }}
        className={`relative rounded-2xl border-2 border-dashed px-6 py-8 text-center transition-colors
          focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-300
          ${off ? 'cursor-default' : 'cursor-pointer'}
          ${over ? 'border-brand-400 bg-brand-50' : 'border-slate-300 bg-slate-50/60 hover:bg-slate-50'}`}
      >
        {fileName && onClear && !busy && (
          <div className="absolute top-3 right-3" onClick={(e) => e.stopPropagation()}>
            <Button variant="secondary" size="sm" icon={RotateCcw} onClick={onClear}>ล้าง</Button>
          </div>
        )}
        <div className="flex flex-col items-center gap-2">
          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${over ? 'bg-brand-100 text-brand-700' : 'bg-white text-slate-400 border border-slate-200'}`}>
            {busy ? <Loader2 size={22} className="animate-spin" /> : fileName ? <FileIcon size={22} /> : <Upload size={22} />}
          </div>
          {busy ? (
            <div className="text-[14px] text-slate-600">กำลังอ่านไฟล์…</div>
          ) : fileName ? (
            <>
              <div className="text-[14px] font-medium text-slate-800 break-all">{fileName}</div>
              <div className="text-[12.5px] text-slate-500">ลากไฟล์ใหม่มาวาง หรือคลิกเพื่อเปลี่ยนไฟล์</div>
            </>
          ) : (
            <>
              <div className="text-[14.5px] font-medium text-slate-700">{label}</div>
              <div className="text-[12.5px] text-slate-500">{hint ?? 'หรือคลิกเพื่อเลือกไฟล์ · ไฟล์ไม่ถูกอัปโหลดไปไหน อ่านในเครื่องเท่านั้น'}</div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
